/**
 * What a release publishes so a user can run an AFAR Environment: one
 * Environment Service Executable per platform, and the versionless install
 * script that fetches one.
 *
 * These sit beside the native Desktop artifacts in the manifest rather than
 * inside them. A Desktop Client's update offer must not turn on whether some
 * other platform's command-line binary made it into the release; they are
 * different products of the same release, bound separately.
 */

export interface EnvironmentServiceExecutableTarget {
  readonly platform: string;
  readonly architecture: string;
  readonly status: "supported" | "experimental";
}

/**
 * The matrix. macOS x64 is supported and verified under Rosetta, which is the
 * bar its predecessor's qualification already ran at. Windows x64 is here so
 * that its cell fails closed rather than being quietly absent.
 */
export const ENVIRONMENT_SERVICE_EXECUTABLE_TARGETS = [
  { platform: "darwin", architecture: "arm64", status: "supported" },
  { platform: "darwin", architecture: "x64", status: "supported" },
  { platform: "linux", architecture: "x64", status: "experimental" },
  { platform: "win32", architecture: "x64", status: "experimental" },
] as const satisfies readonly EnvironmentServiceExecutableTarget[];

/**
 * The asset is the executable itself, not an archive. An archive would need a
 * second digest for the file inside it, and on macOS it does not dodge
 * quarantine anyway - Archive Utility propagates it to what it extracts.
 */
export const environmentServiceExecutableFileName = (
  applicationRelease: string,
  target: Pick<EnvironmentServiceExecutableTarget, "platform" | "architecture">,
): string =>
  environmentServiceExecutableBaseName(
    target.platform,
    `afar-research-${applicationRelease}-${target.platform}-${target.architecture}`,
  );

/**
 * What the file is called once it is installed, bundled, or built: the program's
 * own name, plus whatever the platform requires of an executable's name.
 */
export const environmentServiceExecutableBaseName = (
  platform: string,
  stem = "afar-research",
): string => (platform === "win32" || platform === "win" ? `${stem}.exe` : stem);

export interface ApplicationReleaseEnvironmentServiceExecutable extends EnvironmentServiceExecutableTarget {
  readonly fileName: string;
  readonly size: number;
  readonly sha256: string;
}

/**
 * The install command is versionless - `releases/latest/download/install.sh` -
 * so the asset name is too. The script resolves the release it is installing
 * from the redirect that served it.
 */
export const ENVIRONMENT_SERVICE_INSTALLERS = [
  { shell: "sh", fileName: "install.sh" },
  { shell: "powershell", fileName: "install.ps1" },
] as const;

export interface ApplicationReleaseInstaller {
  readonly shell: string;
  readonly fileName: string;
  readonly size: number;
  readonly sha256: string;
}

const SHA256 = /^[0-9a-f]{64}$/u;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const hasIdentity = (candidate: Record<string, unknown>, fileName: string): boolean =>
  candidate.fileName === fileName &&
  typeof candidate.sha256 === "string" &&
  SHA256.test(candidate.sha256) &&
  Number.isSafeInteger(candidate.size) &&
  Number(candidate.size) >= 1;

export const isApplicationReleaseEnvironmentServiceExecutables = (
  value: unknown,
  applicationRelease: string,
): value is readonly ApplicationReleaseEnvironmentServiceExecutable[] => {
  if (!Array.isArray(value) || value.length === 0) return false;
  const seen = new Set<string>();
  for (const candidate of value) {
    if (!isRecord(candidate)) return false;
    const target = ENVIRONMENT_SERVICE_EXECUTABLE_TARGETS.find(
      (known) =>
        known.platform === candidate.platform && known.architecture === candidate.architecture,
    );
    if (target === undefined || candidate.status !== target.status) return false;
    if (!hasIdentity(candidate, environmentServiceExecutableFileName(applicationRelease, target))) {
      return false;
    }
    const key = `${target.platform}/${target.architecture}`;
    if (seen.has(key)) return false;
    seen.add(key);
  }
  return true;
};

export const isApplicationReleaseInstallers = (
  value: unknown,
): value is readonly ApplicationReleaseInstaller[] => {
  if (!Array.isArray(value) || value.length === 0) return false;
  const seen = new Set<string>();
  for (const candidate of value) {
    if (!isRecord(candidate)) return false;
    const installer = ENVIRONMENT_SERVICE_INSTALLERS.find(
      (known) => known.shell === candidate.shell,
    );
    if (installer === undefined || !hasIdentity(candidate, installer.fileName)) return false;
    if (seen.has(installer.shell)) return false;
    seen.add(installer.shell);
  }
  return true;
};
